'use client';

import { trackEvent } from '@/lib/mixpanel';
import { RadioGroup } from '@headlessui/react';
import { CheckCircleIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { captureException } from '@sentry/nextjs';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useAuth } from '../../contexts/AuthContextNext';
import { useRegister } from '../../hooks/useAuth';
import { useSubscriptionStatus } from '../../hooks/useSettings';
import { trackFormSubmission, trackPageView, trackRegistration, trackStripeRedirect } from '../../lib/analytics';
import { getPlanDisplayPrice, getPlanPaymentLink, PRICING_PLANS } from '../../types/pricing';
import PhoneNumberInput from '../../components/PhoneNumberInput';

const registerSchema = z.object({
  email: z.string()
    .email('Invalid email')
    .min(1, 'Email is required')
    .max(100, 'Email too long'),
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .max(100, 'Password too long'),
  businessName: z.string()
    .min(1, 'Business name is required')
    .max(100, 'Business name too long'),
  website: z.string()
    .optional()
    .refine((val) => !val || /^https?:\/\/.+\..+/.test(val), {
      message: 'Please enter a valid website URL (e.g., https://example.com)',
    }),
  phoneNumber: z.string()
    .optional()
    .refine((val) => !val || /^\+?[1-9]\d{1,14}$/.test(val.replace(/[\s\-()]/g, '')), {
      message: 'Please enter a valid phone number',
    }),
  selectedPlan: z.string()
    .min(1, 'Please select a plan'),
});

type RegisterFormData = z.infer<typeof registerSchema>;

export default function Register() {
  const [error, setError] = useState('');
  const [, setSelectedPlan] = useState<string>('');
  const router = useRouter();
  const { isAuthenticated, isInitialized, user } = useAuth();
  const pricingContext = { isAuthenticated: !isInitialized || isAuthenticated };
  const registerMutation = useRegister();
  const subscriptionStatus = useSubscriptionStatus(isAuthenticated);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      selectedPlan: 'growth',
    },
  });

  // Redirect if user is already authenticated
  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    if (isAuthenticated) {
      // Wait for subscription status to be fetched
      if (subscriptionStatus.isLoading) {
        return;
      }
      
      // Check if there was an error fetching subscription status
      if (subscriptionStatus.isError) {
        // If we can't verify subscription status, assume they need to complete payment
        setError('Unable to verify payment status. Please try again.');
        return;
      }
      
      // Check if they have a pending subscription (incomplete payment)
      const paymentStatus = subscriptionStatus.data?.status;
      if (paymentStatus === 'pending' || paymentStatus === 'stripe_not_configured' || paymentStatus === 'inactive') {
        // They're logged in but haven't completed payment - show error message
        setError('Your payment is incomplete. Redirecting to payment now...');

        // log to mixpanel
        trackEvent('payment_incomplete_redirect', {
          payment_status: paymentStatus,
          email: user?.email || '',
          business_name: user?.businessName || '',
          business_id: user?.businessId || '',
        });

        // Redirect to Stripe with their email
        const selectedPlanData = PRICING_PLANS.find(plan => plan.id === 'growth');
        if (selectedPlanData) {
          const displayPrice = getPlanDisplayPrice(selectedPlanData, { isAuthenticated: true });
          const paymentUrl = new URL(getPlanPaymentLink(selectedPlanData, { isAuthenticated: true }));

          // Get email from stored user or let backend handle it since they're authenticated
          paymentUrl.searchParams.set('prefilled_email', user?.email || '');

          const successUrl = new URL('/registration-complete', window.location.origin);
          successUrl.searchParams.set('plan_name', selectedPlanData.name);
          successUrl.searchParams.set('plan_price', displayPrice.pricePerMonthUSD.toString());

          paymentUrl.searchParams.set('success_url', successUrl.toString());

          setTimeout(() => {
            window.location.href = paymentUrl.toString();
          }, 2000);
        }
        return;
      }
      
      // User is authenticated with completed payment, redirect to app
      router.push('/app');
    } else {
      // Track register page view
      trackPageView('Register Page', window.location.href, {
        page_name: 'register',
        funnel_step: 'registration'
      });
    }
  }, [isAuthenticated, isInitialized, subscriptionStatus.isLoading, subscriptionStatus.isError, subscriptionStatus.data?.status, router, user]);

  const selectedPlanValue = watch('selectedPlan');

  const onSubmit = async (data: RegisterFormData) => {
    try {
      setError('');
      
      // Track form submission attempt
      trackFormSubmission('registration_form', true, {
        selected_plan: data.selectedPlan,
        business_name: data.businessName
      });
      
      await registerMutation.mutateAsync(data);

      // Track successful registration
      trackRegistration(data.email, data.selectedPlan);

      // After successful registration, redirect to Stripe payment link with trial
      const selectedPlanData = PRICING_PLANS.find(plan => plan.id === data.selectedPlan);
      if (selectedPlanData) {
        const displayPrice = getPlanDisplayPrice(selectedPlanData, { isAuthenticated: false });
        // Track beginning of checkout process
        trackStripeRedirect(selectedPlanData.name, displayPrice.pricePerMonthUSD);
        
        // Redirect to Stripe payment link with user email pre-filled
        // Also add plan info as URL parameters for success page tracking
        const paymentUrl = new URL(getPlanPaymentLink(selectedPlanData, { isAuthenticated: false }));
        paymentUrl.searchParams.set('prefilled_email', data.email);
        
        // Add success URL with tracking parameters
        const successUrl = new URL('/registration-complete', window.location.origin);
        successUrl.searchParams.set('plan_name', selectedPlanData.name);
        successUrl.searchParams.set('plan_price', displayPrice.pricePerMonthUSD.toString());
        paymentUrl.searchParams.set('success_url', successUrl.toString());
        
        window.location.href = paymentUrl.toString();
      } else {
        router.push('/login');
      }
    } catch (error: unknown) {
      // Check if this is a pending payment error from backend
      const errorResponse = error as { response?: { data?: { code?: string } } };

      // log to mixpanel
      trackEvent('registration_failed', {
        email: data.email,
        business_name: data.businessName,
        business_id: user?.businessId || '',
        error: errorResponse.response?.data?.code || '',
      });

      // log to sentry
      captureException(error);
      
      if (errorResponse.response?.data?.code === 'PENDING_PAYMENT') {
        // Backend detected pending payment - redirect to Stripe
        setError('Your payment is incomplete. Redirecting to payment now...');
        const selectedPlanData = PRICING_PLANS.find(plan => plan.id === data.selectedPlan);
        if (selectedPlanData) {
          const displayPrice = getPlanDisplayPrice(selectedPlanData, { isAuthenticated: false });
          const paymentUrl = new URL(getPlanPaymentLink(selectedPlanData, { isAuthenticated: false }));
          paymentUrl.searchParams.set('prefilled_email', data.email);
          const successUrl = new URL('/registration-complete', window.location.origin);
          successUrl.searchParams.set('plan_name', selectedPlanData.name);
          successUrl.searchParams.set('plan_price', displayPrice.pricePerMonthUSD.toString());
          paymentUrl.searchParams.set('success_url', successUrl.toString());
          setTimeout(() => {
            window.location.href = paymentUrl.toString();
          }, 2000);
        }
      } else {
        setError('Registration failed');
        
        // Track failed registration
        trackFormSubmission('registration_form', false, {
          selected_plan: data.selectedPlan,
          error: 'registration_failed'
        });
      }
    }
  };

  return (
    <div className="flex min-h-screen flex-col justify-center py-6 sm:py-8 lg:py-12 px-4 sm:px-6 lg:px-8 bg-background">
      <div className="sm:mx-auto sm:w-full sm:max-w-2xl lg:max-w-6xl">
        <Link href="/" className="block">
          <img
            className="mx-auto h-16 sm:h-20 w-auto"
            src="/greet_logo_small.svg"
            alt="Greet Logo"
          />
        </Link>
        <h2 className="mt-4 sm:mt-6 text-center text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
          Create your account
        </h2>
        <p className="mt-2 text-center text-sm text-muted-foreground">
          Start your 7-day free trial today
        </p>
        <p className="mt-1 text-center text-sm text-muted-foreground">
          Or{' '}
          <Link
            href="/login"
            className="font-medium text-primary hover:text-primary/90 transition-colors"
          >
            sign in to your existing account
          </Link>
        </p>
      </div>

      <div className="mt-6 sm:mt-8 mx-auto w-full max-w-2xl lg:max-w-6xl">
        <div className="bg-card shadow-sm rounded-lg border">
          <form className="p-6 sm:p-8" onSubmit={handleSubmit(onSubmit)}>
            {error && (
              <div className="rounded-md bg-destructive/10 p-4 mb-6">
                <div className="text-sm text-destructive">{error}</div>
              </div>
            )}

             <div className="mb-8">
               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                 <div>
                   <label
                     htmlFor="businessName"
                     className="block text-sm font-medium text-foreground"
                   >
                     Business name
                   </label>
                   <div className="mt-1">
                     <input
                       id="businessName"
                       type="text"
                       autoComplete="organization"
                       className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                       {...register('businessName')}
                     />
                     {errors.businessName && (
                       <p className="mt-1 text-sm text-destructive">
                         {errors.businessName.message}
                       </p>
                     )}
                   </div>
                 </div>

                 <div>
                   <label
                     htmlFor="website"
                     className="block text-sm font-medium text-foreground"
                   >
                     Business website <span className="text-sm font-normal text-muted-foreground">(optional)</span>
                   </label>
                   <div className="mt-1">
                     <input
                       id="website"
                       type="url"
                       autoComplete="url"
                       className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                       {...register('website')}
                     />
                     {errors.website && (
                       <p className="mt-1 text-sm text-destructive">
                         {errors.website.message}
                       </p>
                     )}
                   </div>
                 </div>
               </div>
             </div>

            <div className="mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-foreground"
                  >
                    Email address
                  </label>
                  <div className="mt-1">
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      {...register('email')}
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-destructive">
                        {errors.email.message}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-foreground"
                  >
                    Password
                  </label>
                  <div className="mt-1">
                    <input
                      id="password"
                      type="password"
                      autoComplete="new-password"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      {...register('password')}
                    />
                    {errors.password && (
                      <p className="mt-1 text-sm text-destructive">
                        {errors.password.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <div className="space-y-6">
                <div>
                  <label
                    htmlFor="phoneNumber"
                    className="block text-sm font-medium text-foreground"
                  >
                    Phone number <span className="text-sm font-normal text-muted-foreground">(optional)</span>
                  </label>
                  <div className="mt-1">
                    <Controller
                      name="phoneNumber"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <PhoneNumberInput
                          id="phoneNumber"
                          value={value}
                          onChange={onChange}
                        />
                      )}
                    />
                    {errors.phoneNumber && (
                      <p className="mt-1 text-sm text-destructive">
                        {errors.phoneNumber.message}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-muted-foreground">
                      Never used for spam! Only for call forwarding & summaries
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Subscription Plan Selection */}
            <div>
              <label htmlFor="plan-selection" className="block text-sm font-medium text-foreground mb-4">
                Choose your plan
              </label>
              <RadioGroup 
                value={selectedPlanValue} 
                onChange={(value) => {
                  setSelectedPlan(value);
                  setValue('selectedPlan', value);
                  trackEvent('Plan Selected', { plan: value, page: 'register' });
                }}
                id="plan-selection"
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
              >
                {PRICING_PLANS.map((plan) => {
                  const displayPrice = getPlanDisplayPrice(plan, pricingContext);

                  return (
                    <RadioGroup.Option
                      key={plan.id}
                      value={plan.id}
                      className={({ checked }) => 
                        `relative w-full text-left border rounded-lg p-4 cursor-pointer transition-all ${
                          checked
                            ? 'border-primary bg-primary/5'
                            : 'border-input hover:border-primary/50'
                        }`
                      }
                    >
                      {({ checked }) => (
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                checked ? 'border-primary bg-primary' : 'border-input'
                              }`}>
                                {checked && <div className="w-2 h-2 bg-white rounded-full"></div>}
                              </div>
                              <h3 className="font-semibold text-foreground">{plan.name}</h3>
                              {displayPrice.showSale ? (
                                <span className="text-sm">
                                  <span className="text-muted-foreground line-through mr-1">{displayPrice.basePrice}</span>
                                  <span className="font-semibold text-green-700">{displayPrice.price}</span>
                                </span>
                              ) : (
                                <span className="text-sm text-muted-foreground">({plan.price})</span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-1">{plan.subtitle}</p>
                            <div className="mt-3 flex flex-wrap gap-2">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <CheckCircleIcon className="h-3 w-3 mr-1" />
                                7-day free trial
                              </span>
                              {displayPrice.showSale && displayPrice.label && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                                  {displayPrice.label}
                                </span>
                              )}
                              {plan.id === 'growth' && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  <SparklesIcon className="h-3 w-3 mr-1" />
                                  Most Popular
                                </span>
                              )}
                            </div>
                            <ul className="mt-3 space-y-1">
                              {plan.features.slice(0, 6).map((feature, index) => (
                                <li key={index} className="text-sm text-muted-foreground flex items-center">
                                  <CheckCircleIcon className="h-3 w-3 text-green-500 mr-2 flex-shrink-0" />
                                  {feature}
                                </li>
                              ))}
                              {plan.features.length > 6 && (
                                <li className="text-sm text-muted-foreground">
                                  +{plan.features.length - 6} more features
                                </li>
                              )}
                            </ul>
                          </div>
                        </div>
                      )}
                    </RadioGroup.Option>
                  );
                })}
              </RadioGroup>
              {errors.selectedPlan && (
                <p className="text-sm text-destructive mt-2">
                  {errors.selectedPlan.message}
                </p>
              )}
            </div>

            <div className="mt-8">
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground shadow hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:pointer-events-none disabled:opacity-50"
              >
                {isSubmitting ? 'Creating account...' : 'Create account & start free trial'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
} 
